import type { MtLeavesPageInfo, MtLeavesResponse } from './types';

const MT_LEAVES_CURSOR_SCHEMA_VERSION = 1;
const MT_LEAVES_CURSOR_KEY_PREFIX = 'pp.mtLeaves.cursor.v1';
const MT_LEAVES_DB_NAME = 'pp-public-mt-leaves-cache';
const MT_LEAVES_DB_VERSION = 1;
const MT_LEAVES_STORE_NAME = 'mtLeaves';
const MT_LEAVES_MAX_SYNC_PAGES = 1000;

type MtLeavesCursorMeta = {
  schemaVersion: number;
  chainId: number;
  scope: string;
  snapshotIndexedToBlock: string;
  nextCursor: string | null;
  updatedAt: number;
};

type MtLeavesCacheRecord = {
  id: string;
  schemaVersion: number;
  chainId: number;
  scope: string;
  snapshotIndexedToBlock: string;
  aspLeaves: string[];
  stateTreeLeaves: string[];
  updatedAt: number;
};

type FetchMtLeavesPage = (params: { cursor?: string; limit: number }) => Promise<MtLeavesResponse>;

type SyncMtLeavesParams = {
  chainId: number;
  scope: string;
  limit: number;
  fetchPage: FetchMtLeavesPage;
};

type ParsedCursorPayload = {
  chainId: number;
  scope: string;
  snapshotIndexedToBlock: string;
  aspOffset: number;
  aspDone: boolean;
  stateDone: boolean;
};

const CURSOR_RESET_ERROR_CODES = new Set([
  'invalid_cursor',
  'invalid_cursor_signature',
  'invalid_cursor_payload',
  'cursor_chain_mismatch',
  'cursor_scope_mismatch',
  'cursor_schema_mismatch',
  'invalid_cursor_snapshot',
]);

const memoryLeavesCache = new Map<string, MtLeavesCacheRecord>();
const memoryCursorCache = new Map<string, MtLeavesCursorMeta>();
let dbPromise: Promise<IDBDatabase | null> | null = null;

const getCacheId = (chainId: number, scope: string): string => `${chainId}:${scope}`;
const getCursorStorageKey = (chainId: number, scope: string): string =>
  `${MT_LEAVES_CURSOR_KEY_PREFIX}:${chainId}:${scope}`;

const getLocalStorageSafe = (): Storage | null => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }
  return window.localStorage;
};

const openCacheDb = async (): Promise<IDBDatabase | null> => {
  if (typeof indexedDB === 'undefined') {
    return null;
  }

  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(MT_LEAVES_DB_NAME, MT_LEAVES_DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(MT_LEAVES_STORE_NAME)) {
          db.createObjectStore(MT_LEAVES_STORE_NAME, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('indexeddb_open_failed'));
    });
  }

  return dbPromise;
};

const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexeddb_request_failed'));
  });

const transactionDone = (transaction: IDBTransaction): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('indexeddb_tx_failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('indexeddb_tx_aborted'));
  });

const readCursorMeta = (chainId: number, scope: string): MtLeavesCursorMeta | null => {
  const storage = getLocalStorageSafe();
  const key = getCursorStorageKey(chainId, scope);
  if (!storage) {
    return memoryCursorCache.get(key) ?? null;
  }

  const raw = storage.getItem(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<MtLeavesCursorMeta>;
    if (
      parsed.schemaVersion !== MT_LEAVES_CURSOR_SCHEMA_VERSION ||
      parsed.chainId !== chainId ||
      parsed.scope !== scope ||
      typeof parsed.snapshotIndexedToBlock !== 'string' ||
      (typeof parsed.nextCursor !== 'string' && parsed.nextCursor !== null) ||
      typeof parsed.updatedAt !== 'number'
    ) {
      storage.removeItem(key);
      return null;
    }

    return parsed as MtLeavesCursorMeta;
  } catch {
    storage.removeItem(key);
    return null;
  }
};

const writeCursorMeta = (meta: MtLeavesCursorMeta): void => {
  const key = getCursorStorageKey(meta.chainId, meta.scope);
  const storage = getLocalStorageSafe();
  if (!storage) {
    memoryCursorCache.set(key, meta);
    return;
  }
  storage.setItem(key, JSON.stringify(meta));
};

const clearCursorMeta = (chainId: number, scope: string): void => {
  const key = getCursorStorageKey(chainId, scope);
  const storage = getLocalStorageSafe();
  if (!storage) {
    memoryCursorCache.delete(key);
    return;
  }
  storage.removeItem(key);
};

const readLeavesCache = async (chainId: number, scope: string): Promise<MtLeavesCacheRecord | null> => {
  const cacheId = getCacheId(chainId, scope);
  const db = await openCacheDb();
  if (!db) {
    return memoryLeavesCache.get(cacheId) ?? null;
  }

  const tx = db.transaction(MT_LEAVES_STORE_NAME, 'readonly');
  const store = tx.objectStore(MT_LEAVES_STORE_NAME);
  const record = await requestToPromise(store.get(cacheId) as IDBRequest<MtLeavesCacheRecord | undefined>);
  return record ?? null;
};

const writeLeavesCache = async (
  chainId: number,
  scope: string,
  payload: {
    snapshotIndexedToBlock: string;
    aspLeaves: string[];
    stateTreeLeaves: string[];
  },
): Promise<void> => {
  const cacheId = getCacheId(chainId, scope);
  const record: MtLeavesCacheRecord = {
    id: cacheId,
    schemaVersion: MT_LEAVES_CURSOR_SCHEMA_VERSION,
    chainId,
    scope,
    snapshotIndexedToBlock: payload.snapshotIndexedToBlock,
    aspLeaves: payload.aspLeaves,
    stateTreeLeaves: payload.stateTreeLeaves,
    updatedAt: Date.now(),
  };

  const db = await openCacheDb();
  if (!db) {
    memoryLeavesCache.set(cacheId, record);
    return;
  }

  const tx = db.transaction(MT_LEAVES_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MT_LEAVES_STORE_NAME);
  store.put(record);
  await transactionDone(tx);
};

const clearLeavesCache = async (chainId: number, scope: string): Promise<void> => {
  const cacheId = getCacheId(chainId, scope);
  const db = await openCacheDb();
  if (!db) {
    memoryLeavesCache.delete(cacheId);
    return;
  }

  const tx = db.transaction(MT_LEAVES_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MT_LEAVES_STORE_NAME);
  store.delete(cacheId);
  await transactionDone(tx);
};

const clearSyncState = async (chainId: number, scope: string): Promise<void> => {
  clearCursorMeta(chainId, scope);
  await clearLeavesCache(chainId, scope);
};

const extractErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const candidate = error as { code?: unknown };
  return typeof candidate.code === 'string' ? candidate.code : undefined;
};

const extractErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const candidate = error as { status?: unknown };
  return typeof candidate.status === 'number' ? candidate.status : undefined;
};

const shouldResetFromCursorError = (error: unknown): boolean => {
  const status = extractErrorStatus(error);
  const code = extractErrorCode(error);
  return status === 400 && !!code && CURSOR_RESET_ERROR_CODES.has(code);
};

const parseCursorPayload = (cursor: string | null | undefined): ParsedCursorPayload | null => {
  if (!cursor) return null;
  const [encodedPayload] = cursor.split('.');
  if (!encodedPayload) return null;
  if (typeof globalThis.atob !== 'function') return null;

  try {
    const normalized = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const parsed = JSON.parse(globalThis.atob(padded)) as Partial<ParsedCursorPayload>;
    if (
      typeof parsed.chainId !== 'number' ||
      !Number.isInteger(parsed.chainId) ||
      typeof parsed.scope !== 'string' ||
      typeof parsed.snapshotIndexedToBlock !== 'string' ||
      typeof parsed.aspOffset !== 'number' ||
      !Number.isInteger(parsed.aspOffset) ||
      parsed.aspOffset < 0 ||
      typeof parsed.aspDone !== 'boolean' ||
      typeof parsed.stateDone !== 'boolean'
    ) {
      return null;
    }
    return {
      chainId: parsed.chainId,
      scope: parsed.scope,
      snapshotIndexedToBlock: parsed.snapshotIndexedToBlock,
      aspOffset: parsed.aspOffset,
      aspDone: parsed.aspDone,
      stateDone: parsed.stateDone,
    };
  } catch {
    return null;
  }
};

const appendPage = (current: string[], incoming: string[]): string[] => {
  if (incoming.length === 0) return current;
  return current.concat(incoming);
};

const ensureValidSnapshot = (
  previousSnapshot: string | null,
  currentPageInfo: MtLeavesPageInfo,
  usedCursorForThisPage: boolean,
): boolean => {
  if (!usedCursorForThisPage || !previousSnapshot) {
    return true;
  }
  return previousSnapshot === currentPageInfo.snapshotIndexedToBlock;
};

export const syncMtLeavesWithCache = async ({
  chainId,
  scope,
  limit,
  fetchPage,
}: SyncMtLeavesParams): Promise<MtLeavesResponse> => {
  const cachedCursor = readCursorMeta(chainId, scope);
  const cachedLeaves = await readLeavesCache(chainId, scope);

  const hasUsableCheckpoint =
    !!cachedCursor &&
    typeof cachedCursor.nextCursor === 'string' &&
    cachedCursor.nextCursor.length > 0 &&
    !!cachedLeaves &&
    cachedLeaves.schemaVersion === MT_LEAVES_CURSOR_SCHEMA_VERSION;

  let aggregatedAspLeaves = hasUsableCheckpoint ? [...(cachedLeaves?.aspLeaves ?? [])] : [];
  let aggregatedStateTreeLeaves = hasUsableCheckpoint ? [...(cachedLeaves?.stateTreeLeaves ?? [])] : [];
  let nextCursor = hasUsableCheckpoint ? (cachedCursor?.nextCursor ?? undefined) : undefined;
  let expectedSnapshot = hasUsableCheckpoint ? (cachedCursor?.snapshotIndexedToBlock ?? null) : null;
  let rebuiltFromScratch = !hasUsableCheckpoint;
  const checkpointCursorPayload = parseCursorPayload(cachedCursor?.nextCursor);
  const hasConsistentCheckpoint =
    hasUsableCheckpoint &&
    !!checkpointCursorPayload &&
    checkpointCursorPayload.chainId === chainId &&
    checkpointCursorPayload.scope === scope &&
    checkpointCursorPayload.snapshotIndexedToBlock === cachedCursor.snapshotIndexedToBlock &&
    checkpointCursorPayload.aspOffset === aggregatedAspLeaves.length;
  const canAdvanceSnapshotFromCompletedCheckpoint =
    hasConsistentCheckpoint && !!checkpointCursorPayload?.aspDone && !!checkpointCursorPayload?.stateDone;

  if (!hasConsistentCheckpoint) {
    await clearSyncState(chainId, scope);
    aggregatedAspLeaves = [];
    aggregatedStateTreeLeaves = [];
    nextCursor = undefined;
    expectedSnapshot = null;
    rebuiltFromScratch = true;
  }

  for (let pageIndex = 0; pageIndex < MT_LEAVES_MAX_SYNC_PAGES; pageIndex += 1) {
    const usedCursorForThisPage = typeof nextCursor === 'string' && nextCursor.length > 0;

    let page: MtLeavesResponse;
    try {
      page = await fetchPage({ cursor: nextCursor, limit });
    } catch (error) {
      if (!rebuiltFromScratch && usedCursorForThisPage && shouldResetFromCursorError(error)) {
        await clearSyncState(chainId, scope);
        aggregatedAspLeaves = [];
        aggregatedStateTreeLeaves = [];
        nextCursor = undefined;
        expectedSnapshot = null;
        rebuiltFromScratch = true;
        continue;
      }
      throw error;
    }

    if (!page.pageInfo) {
      const aspLeaves = page.aspLeaves ?? [];
      const stateTreeLeaves = page.stateTreeLeaves ?? [];
      clearCursorMeta(chainId, scope);
      await writeLeavesCache(chainId, scope, {
        snapshotIndexedToBlock: expectedSnapshot ?? '0',
        aspLeaves,
        stateTreeLeaves,
      });
      return {
        aspLeaves,
        stateTreeLeaves,
      };
    }

    if (!ensureValidSnapshot(expectedSnapshot, page.pageInfo, usedCursorForThisPage)) {
      if (canAdvanceSnapshotFromCompletedCheckpoint && usedCursorForThisPage && pageIndex === 0) {
        expectedSnapshot = page.pageInfo.snapshotIndexedToBlock;
      } else {
        if (rebuiltFromScratch) {
          throw new Error('mt_leaves_snapshot_mismatch');
        }
        await clearSyncState(chainId, scope);
        aggregatedAspLeaves = [];
        aggregatedStateTreeLeaves = [];
        nextCursor = undefined;
        expectedSnapshot = null;
        rebuiltFromScratch = true;
        continue;
      }
    }

    aggregatedAspLeaves = appendPage(aggregatedAspLeaves, page.aspLeaves ?? []);
    aggregatedStateTreeLeaves = appendPage(aggregatedStateTreeLeaves, page.stateTreeLeaves ?? []);

    expectedSnapshot = page.pageInfo.snapshotIndexedToBlock;
    nextCursor = page.pageInfo.nextCursor ?? undefined;

    await writeLeavesCache(chainId, scope, {
      snapshotIndexedToBlock: expectedSnapshot,
      aspLeaves: aggregatedAspLeaves,
      stateTreeLeaves: aggregatedStateTreeLeaves,
    });
    writeCursorMeta({
      schemaVersion: MT_LEAVES_CURSOR_SCHEMA_VERSION,
      chainId,
      scope,
      snapshotIndexedToBlock: expectedSnapshot,
      nextCursor: page.pageInfo.nextCursor ?? null,
      updatedAt: Date.now(),
    });

    if (!page.pageInfo.hasMore) {
      return {
        aspLeaves: aggregatedAspLeaves,
        stateTreeLeaves: aggregatedStateTreeLeaves,
        pageInfo: page.pageInfo,
      };
    }
  }

  throw new Error('mt_leaves_sync_page_limit_exceeded');
};

export const clearMtLeavesCacheForScope = async (chainId: number, scope: string): Promise<void> => {
  await clearSyncState(chainId, scope);
};
